import type {
  TCommunicationChannel,
  TManualSymptomsCamel,
  TScheduleType,
} from './appointments';
import type { TNullable } from './common';

export enum EAgeUnit {
  year = 'year',
}

enum ETimeUnit {
  minute = 'minute',
  hour = 'hour',
  day = 'day',
  week = 'week',
}

export type TAppointmentInsuranceResult =
  | {
      type: 'yes';
      id: string;
      isPremium: boolean;
    }
  | {
      type: 'no';
    };

export interface IAppointmentResultFormData {
  location?: {
    country: string;
    subdivision: string;
  };
  institutionId: string;
  infermedicaSurveyId?: string;
  isabelSurveyId?: string;
  communicationChannel: TCommunicationChannel;
  scheduleType: TScheduleType;
  institutionServiceId?: string;
  duration: TNullable<number>;
  participants: {
    // user.id not user_institution_role_id
    provider: TNullable<string>;
    patient: TNullable<string>;
  };
  start?: string;
  consented: boolean;
  documentIds: string[];
  insurance: TNullable<TAppointmentInsuranceResult>;
  manualSymptoms?: TManualSymptomsCamel;
  kioskId?: TNullable<string>;
}

export interface IInfemedicaQuestionBaseProps<T> {
  id: string;
  required?: boolean;
  defaultValue?: T;
}

export interface IInfemedicaChoice {
  id: string;
  label: string;
}

export interface IInfemedicaKnowledgeItem {
  id: string;
  name: string;
}

export interface IInfemedicaSymptomChoice {
  id: string;
  name: string;
  choices: IInfemedicaChoice[];
}

type TInfemedicaNumberAnswer<U> = { value: number; unit: U };
export type TInfemedicaChoiceAnswer = { id: string };
export type TInfemedicaMultipleChoiceAnswer = TInfemedicaChoiceAnswer[];
export type TInfemedicaAgeAnswer = TInfemedicaNumberAnswer<EAgeUnit>;
export type TInfemedicaListAnswer = TInfemedicaChoiceAnswer[];
export type TInfemedicaTextAnswer = string;
export type TInfemedicaSymptomAnswer = Array<{ id: string; choice_id: string }>;
type TInfemedicaDurationAnswer = Array<{
  id: string;
  duration: TInfemedicaNumberAnswer<ETimeUnit>;
}>;

export type TInfemedicaAnswer =
  | TInfemedicaChoiceAnswer
  | TInfemedicaMultipleChoiceAnswer
  | TInfemedicaAgeAnswer
  | TInfemedicaListAnswer
  | TInfemedicaTextAnswer
  | TInfemedicaSymptomAnswer
  | TInfemedicaDurationAnswer;

export interface IInfemedicaKnowledgeResponse {
  self: string;
  first: string;
  prev: string;
  next: string;
  last: string;
  items: IInfemedicaKnowledgeItem[];
}

export enum EInfemedicaSurveyComponent {
  groupMultiple = 'group_multiple',
  single = 'single',
  groupSingle = 'group_single',
}

export enum EInfermedicaAnswerType {
  multipleChoice = 'multiple_choice',
  choice = 'choice',
  age = 'age',
  list = 'list',
  text = 'text',
  symptoms = 'symptoms',
  duration = 'duration',
}

interface IInfermedicaAlert {
  type: 'soft_stop';
  reasons: ('suicide_attempt' | 'suicidal_intent' | 'suicidal_thoughts')[];
}

interface IInfemedicaQuestionMeta {
  has_rationale: boolean;
  required?: boolean;
  alerts?: IInfermedicaAlert[];
}

interface IInfemedicaChoiceQuestionMeta extends IInfemedicaQuestionMeta {
  answers: IInfemedicaChoice[];
}

interface IInfemedicaAgeQuestionMeta extends IInfemedicaQuestionMeta {
  min_value?: number;
  max_value?: number;
}

interface IInfemedicaListQuestionMeta extends IInfemedicaQuestionMeta {
  source_list_url: string;
}

interface IInfemedicaTextQuestionMeta extends IInfemedicaQuestionMeta {
  max_length?: number;
}

interface IInfemedicaSymptomQuestionMeta extends IInfemedicaQuestionMeta {
  answers: IInfemedicaSymptomChoice[];
  component: EInfemedicaSurveyComponent;
}

interface IInfemedicaDurationQuestionMeta extends IInfemedicaQuestionMeta {
  evidence_id: string;
}

interface IInfemedicaCreateQuestion<A, M, UA> {
  id: string;
  index: number;
  question: {
    text: string;
  };
  answer_type: A;
  meta: M;
  user_answer?: {
    answer: UA;
  };
}

type TInfemedicaMultipleChoiceQuestion = IInfemedicaCreateQuestion<
  EInfermedicaAnswerType.multipleChoice,
  IInfemedicaChoiceQuestionMeta,
  TInfemedicaMultipleChoiceAnswer
>;
type TInfemedicaChoiceQuestion = IInfemedicaCreateQuestion<
  EInfermedicaAnswerType.choice,
  IInfemedicaChoiceQuestionMeta,
  TInfemedicaChoiceAnswer
>;
type TInfemedicaAgeQuestion = IInfemedicaCreateQuestion<
  EInfermedicaAnswerType.age,
  IInfemedicaAgeQuestionMeta,
  TInfemedicaAgeAnswer
>;
type TInfemedicaListQuestion = IInfemedicaCreateQuestion<
  EInfermedicaAnswerType.list,
  IInfemedicaListQuestionMeta,
  TInfemedicaListAnswer
>;
type TInfemedicaTextQuestion = IInfemedicaCreateQuestion<
  EInfermedicaAnswerType.text,
  IInfemedicaTextQuestionMeta,
  TInfemedicaTextAnswer
>;
type TInfemedicaSymptomsQuestion = IInfemedicaCreateQuestion<
  EInfermedicaAnswerType.symptoms,
  IInfemedicaSymptomQuestionMeta,
  TInfemedicaSymptomAnswer
>;
type TInfemedicaDurationQuestion = IInfemedicaCreateQuestion<
  EInfermedicaAnswerType.duration,
  IInfemedicaDurationQuestionMeta,
  TInfemedicaDurationAnswer
>;

export type TInfemedicaQuestion =
  | TInfemedicaMultipleChoiceQuestion
  | TInfemedicaChoiceQuestion
  | TInfemedicaAgeQuestion
  | TInfemedicaListQuestion
  | TInfemedicaTextQuestion
  | TInfemedicaSymptomsQuestion
  | TInfemedicaDurationQuestion;

type TSurveyResultsRecommended = {
  recommended_specialist?: string;
  specialist: { id: string; name: string };
};

export type TSurveyResults = TSurveyResultsRecommended;

export type ISymptoms = {
  id: string;
  infermedica_id: string;
  labels: Record<string, string>;
};

export type TInfermedicaSymptomsResponse = {
  data: ISymptoms[];
  success: boolean;
  meta: Record<string, unknown>;
  error: Record<string, unknown>;
};
